import React, { useState, useEffect, useRef, useCallback } from 'react';
import { customersApi, productsApi, ordersApi } from '../services/api';
import { useToast } from './Toast';
import {
  COUNTRIES,
  getRegionOptions,
  getRegionLabel,
  getPostalCodeLabel,
  getCountryName,
  isStateRequired,
  isPostalCodeRequired,
} from '../utils/addressData';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  UserIcon,
  ShoppingBagIcon,
  TruckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Types
interface Customer {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  default_address_line1?: string;
  default_address_line2?: string;
  default_city?: string;
  default_state?: string;
  default_zip?: string;
  default_country?: string;
  payment_terms?: string;
}

interface ProductResult {
  id: string;
  title: string;
  sku?: string;
  price: number;
  weight?: number;
  image_url?: string;
  hs_tariff_code?: string;
  country_of_origin?: string;
  variants?: VariantResult[];
}

interface VariantResult {
  id: string;
  title: string;
  sku?: string;
  price: number;
  weight?: number;
  option1?: string;
  option2?: string;
  option3?: string;
  inventory_quantity?: number;
  image_url?: string;
  hs_tariff_code?: string;
  country_of_origin?: string;
}

interface LineItem {
  key: string;
  product_id?: string;
  product_variant_id?: string;
  title: string;
  sku: string;
  quantity: number;
  price: number;
  weight_oz: number;
  image_url?: string;
  // Customs info for international shipments (commercial invoice lines)
  hs_tariff_code?: string;
  country_of_origin?: string;
}

interface NewOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderCreated: () => void;
}

// Steps
type Step = 'customer' | 'items' | 'review';

const STEPS: { key: Step; label: string; icon: React.ElementType }[] = [
  { key: 'customer', label: 'Customer & Shipping', icon: UserIcon },
  { key: 'items', label: 'Items', icon: ShoppingBagIcon },
  { key: 'review', label: 'Review', icon: DocumentTextIcon },
];

export const NewOrderModal: React.FC<NewOrderModalProps> = ({ isOpen, onClose, onOrderCreated }) => {
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState<Step>('customer');
  const [submitting, setSubmitting] = useState(false);

  // Customer state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const customerSearchRef = useRef<HTMLInputElement>(null);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  // Manual customer fields (when no existing customer selected)
  const [manualCustomer, setManualCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<ProductResult[]>([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const productSearchRef = useRef<HTMLInputElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  // Line items
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  // Custom item form
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItem, setCustomItem] = useState({ title: '', sku: '', quantity: 1, price: 0, weight_oz: 0, hs_tariff_code: '', country_of_origin: '' });

  // Shipping address
  const [address, setAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });

  // Order details
  const [orderType, setOrderType] = useState<'shipping' | 'pickup'>('shipping');
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<'prepaid' | 'net30' | 'net60'>('prepaid');

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('customer');
      setCustomerSearch('');
      setCustomerResults([]);
      setSelectedCustomer(null);
      setManualCustomer({ first_name: '', last_name: '', email: '', phone: '' });
      setLineItems([]);
      setAddress({ line1: '', line2: '', city: '', state: '', zip: '', country: 'US' });
      setOrderType('shipping');
      setNotes('');
      setPaymentTerms('prepaid');
      setProductSearch('');
      setProductResults([]);
      setShowCustomItemForm(false);
      setCustomItem({ title: '', sku: '', quantity: 1, price: 0, weight_oz: 0, hs_tariff_code: '', country_of_origin: '' });
    }
  }, [isOpen]);

  // Customer search with debounce — skip search if a customer is already selected
  useEffect(() => {
    if (selectedCustomer) return;

    if (!customerSearch || customerSearch.length < 2) {
      setCustomerResults([]);
      setShowCustomerDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const data = await customersApi.list({ search: customerSearch, limit: 8 });
        setCustomerResults(data.customers || []);
        setShowCustomerDropdown(true);
      } catch {
        setCustomerResults([]);
      } finally {
        setSearchingCustomers(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, selectedCustomer]);

  // Product search with debounce
  useEffect(() => {
    if (!productSearch || productSearch.length < 2) {
      setProductResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const data = await productsApi.search(productSearch);
        setProductResults(data.products || []);
        setShowProductDropdown(true);
      } catch {
        setProductResults([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Select customer from search — populate name, address, and payment terms
  const handleSelectCustomer = useCallback((customer: Customer) => {
    setShowCustomerDropdown(false);
    setCustomerResults([]);
    setSelectedCustomer(customer);
    setManualCustomer({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
    });
    setCustomerSearch(`${customer.first_name || ''} ${customer.last_name || ''}`.trim());

    // Auto-fill default shipping address from customer record
    if (customer.default_address_line1) {
      setAddress({
        line1: customer.default_address_line1 || '',
        line2: customer.default_address_line2 || '',
        city: customer.default_city || '',
        state: customer.default_state || '',
        zip: customer.default_zip || '',
        country: customer.default_country || 'US',
      });
    }

    // Auto-fill payment terms from customer record
    if (customer.payment_terms && ['prepaid', 'net30', 'net60'].includes(customer.payment_terms)) {
      setPaymentTerms(customer.payment_terms as 'prepaid' | 'net30' | 'net60');
    }
  }, []);

  // Clear selected customer — also reset address and payment terms
  const handleClearCustomer = useCallback(() => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setManualCustomer({ first_name: '', last_name: '', email: '', phone: '' });
    setAddress({ line1: '', line2: '', city: '', state: '', zip: '', country: 'US' });
    setPaymentTerms('prepaid');
    customerSearchRef.current?.focus();
  }, []);

  // Add product/variant as line item
  const handleAddProduct = useCallback((product: ProductResult, variant?: VariantResult) => {
    const newItem: LineItem = {
      key: `${Date.now()}-${Math.random()}`,
      product_id: product.id,
      product_variant_id: variant?.id,
      title: variant ? `${product.title} - ${variant.title}` : product.title,
      sku: variant?.sku || product.sku || '',
      quantity: 1,
      price: variant?.price || product.price || 0,
      weight_oz: variant?.weight || product.weight || 0,
      image_url: variant?.image_url || product.image_url,
      hs_tariff_code: variant?.hs_tariff_code || product.hs_tariff_code || '',
      country_of_origin: variant?.country_of_origin || product.country_of_origin || '',
    };
    setLineItems(prev => [...prev, newItem]);
    setProductSearch('');
    setProductResults([]);
    setShowProductDropdown(false);
  }, []);

  // Update line item
  const handleUpdateLineItem = useCallback((key: string, field: keyof LineItem, value: string | number) => {
    setLineItems(prev => prev.map(item => 
      item.key === key ? { ...item, [field]: value } : item
    ));
  }, []);

  // Remove line item
  const handleRemoveLineItem = useCallback((key: string) => {
    setLineItems(prev => prev.filter(item => item.key !== key));
  }, []);

  // Add custom/misc line item
  const handleAddCustomItem = useCallback(() => {
    if (!customItem.title.trim()) return;
    const newItem: LineItem = {
      key: `custom-${Date.now()}-${Math.random()}`,
      title: customItem.title.trim(),
      sku: customItem.sku.trim() || 'MISC',
      quantity: customItem.quantity || 1,
      price: Number(customItem.price) || 0,
      weight_oz: Number(customItem.weight_oz) || 0,
      hs_tariff_code: customItem.hs_tariff_code.trim(),
      country_of_origin: customItem.country_of_origin,
    };
    setLineItems(prev => [...prev, newItem]);
    setCustomItem({ title: '', sku: '', quantity: 1, price: 0, weight_oz: 0, hs_tariff_code: '', country_of_origin: '' });
    setShowCustomItemForm(false);
  }, [customItem]);

  // Calculate totals
  const subtotal = lineItems.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0);
  const totalItems = lineItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const totalWeight = lineItems.reduce((sum, item) => sum + (Number(item.weight_oz || 0) * Number(item.quantity || 0)), 0);

  // International shipments need customs info per line item
  const isInternational = orderType === 'shipping' && address.country !== 'US';
  const regionOptions = getRegionOptions(address.country);

  // Changing country invalidates a state code from another country's list
  const handleCountryChange = (country: string) => {
    setAddress(prev => {
      const newOptions = getRegionOptions(country);
      const stateStillValid = newOptions ? newOptions.includes(prev.state) : true;
      return { ...prev, country, state: stateStillValid ? prev.state : '' };
    });
  };

  // Step validation — customer step now includes shipping
  const isCustomerStepValid = () => {
    const hasName = manualCustomer.first_name.trim() !== '' && manualCustomer.last_name.trim() !== '';
    if (!hasName) return false;
    if (orderType === 'pickup') return true;
    if (address.line1.trim() === '' || address.city.trim() === '') return false;
    if (isStateRequired(address.country) && address.state.trim() === '') return false;
    if (isPostalCodeRequired(address.country) && address.zip.trim() === '') return false;
    return true;
  };

  const isItemsStepValid = () => {
    return lineItems.length > 0;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'customer': return isCustomerStepValid();
      case 'items': return isItemsStepValid();
      case 'review': return true;
      default: return false;
    }
  };

  const nextStep = () => {
    const idx = STEPS.findIndex(s => s.key === currentStep);
    if (idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1].key);
    }
  };

  const prevStep = () => {
    const idx = STEPS.findIndex(s => s.key === currentStep);
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1].key);
    }
  };

  // Submit order
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await ordersApi.create({
        customer: {
          id: selectedCustomer?.id,
          first_name: manualCustomer.first_name,
          last_name: manualCustomer.last_name,
          email: manualCustomer.email,
          phone: manualCustomer.phone,
        },
        items: lineItems.map(item => ({
          product_id: item.product_id,
          product_variant_id: item.product_variant_id,
          title: item.title,
          sku: item.sku,
          quantity: item.quantity,
          price: item.price,
          weight_oz: item.weight_oz,
          hs_tariff_code: item.hs_tariff_code?.trim() || undefined,
          country_of_origin: item.country_of_origin || undefined,
        })),
        shipping_address: orderType === 'shipping' ? {
          line1: address.line1,
          line2: address.line2,
          city: address.city,
          state: address.state,
          zip: address.zip,
          country: address.country,
        } : undefined,
        order_type: orderType,
        notes,
        payment_terms: paymentTerms,
      });
      toast.success('Order created successfully');
      onOrderCreated();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const stepIndex = STEPS.findIndex(s => s.key === currentStep);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-strong max-w-5xl w-full max-h-[95vh] flex flex-col m-4 lg:m-6" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-slate-900">New Order</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center mt-6 gap-1">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isActive = i === stepIndex;
              const isCompleted = i < stepIndex;
              return (
                <React.Fragment key={step.key}>
                  <button
                    onClick={() => {
                      // Only allow going back to completed steps
                      if (i < stepIndex) setCurrentStep(step.key);
                    }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-brand-blue text-white'
                        : isCompleted
                        ? 'bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer'
                        : 'bg-slate-50 text-slate-400'
                    }`}
                  >
                    <StepIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{step.label}</span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 rounded ${i < stepIndex ? 'bg-green-300' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 overflow-y-auto flex-1">
          {/* STEP 1: Customer */}
          {currentStep === 'customer' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Customer Information</h3>
                <p className="text-sm text-slate-500">Search for an existing customer or enter new details.</p>
              </div>

              {/* Customer Search */}
              <div className="relative" ref={customerDropdownRef}>
                <label className="block text-xs font-medium text-slate-700 mb-1">Search Customers</label>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    ref={customerSearchRef}
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      if (selectedCustomer) setSelectedCustomer(null);
                    }}
                    placeholder="Type a name, email, or phone..."
                    className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  />
                  {selectedCustomer && (
                    <button
                      onClick={handleClearCustomer}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Customer Dropdown */}
                {showCustomerDropdown && customerResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-medium max-h-60 overflow-y-auto">
                    {customerResults.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => handleSelectCustomer(customer)}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0 transition-colors"
                      >
                        <div className="w-8 h-8 bg-brand-blue-lighter rounded-full flex items-center justify-center flex-shrink-0">
                          <UserIcon className="w-4 h-4 text-brand-blue" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {customer.first_name} {customer.last_name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{customer.email}</div>
                        </div>
                        {customer.phone && (
                          <span className="text-xs text-slate-400 flex-shrink-0">{customer.phone}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {showCustomerDropdown && searchingCustomers && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-medium p-4 text-center text-sm text-slate-500">
                    Searching...
                  </div>
                )}

                {showCustomerDropdown && !searchingCustomers && customerSearch.length >= 2 && customerResults.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-medium p-4 text-center text-sm text-slate-500">
                    No customers found. Enter details below.
                  </div>
                )}
              </div>

              {selectedCustomer && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <UserIcon className="w-4 h-4" />
                  <span>Linked to existing customer: <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong></span>
                </div>
              )}

              {/* Manual fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={manualCustomer.first_name}
                    onChange={(e) => setManualCustomer(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={manualCustomer.last_name}
                    onChange={(e) => setManualCustomer(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={manualCustomer.email}
                    onChange={(e) => setManualCustomer(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={manualCustomer.phone}
                    onChange={(e) => setManualCustomer(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>

              {/* Payment Terms */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Payment Terms</label>
                <div className="flex gap-3">
                  {([
                    { value: 'prepaid', label: 'Prepaid' },
                    { value: 'net30', label: 'Net 30' },
                    { value: 'net60', label: 'Net 60' },
                  ] as const).map(term => (
                    <button
                      key={term.value}
                      onClick={() => setPaymentTerms(term.value)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                        paymentTerms === term.value
                          ? 'bg-brand-blue text-white border-brand-blue'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {term.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Delivery Details</h3>
                <p className="text-sm text-slate-500">Set the delivery method and shipping address.</p>
              </div>

              {/* Order Type */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">Delivery Method</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setOrderType('shipping')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
                      orderType === 'shipping'
                        ? 'bg-brand-blue text-white border-brand-blue'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <TruckIcon className="w-5 h-5" />
                    Ship
                  </button>
                  <button
                    onClick={() => setOrderType('pickup')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
                      orderType === 'pickup'
                        ? 'bg-brand-blue text-white border-brand-blue'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <ShoppingBagIcon className="w-5 h-5" />
                    Pickup
                  </button>
                </div>
              </div>

              {/* Shipping Address Fields */}
              {orderType === 'shipping' && (
                <div className="space-y-4">
                  {selectedCustomer && address.line1 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                      <TruckIcon className="w-4 h-4" />
                      <span>Address loaded from customer profile. You can edit below.</span>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Address Line 1 *</label>
                    <input
                      type="text"
                      value={address.line1}
                      onChange={(e) => setAddress(prev => ({ ...prev, line1: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={address.line2}
                      onChange={(e) => setAddress(prev => ({ ...prev, line2: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                      placeholder="Apt, suite, unit, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Country *</label>
                    <select
                      value={address.country}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent bg-white"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-6 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-700 mb-1">City *</label>
                      <input
                        type="text"
                        value={address.city}
                        onChange={(e) => setAddress(prev => ({ ...prev, city: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        placeholder="City"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        {getRegionLabel(address.country)}{isStateRequired(address.country) ? ' *' : ''}
                      </label>
                      {regionOptions ? (
                        <select
                          value={address.state}
                          onChange={(e) => setAddress(prev => ({ ...prev, state: e.target.value }))}
                          className="w-full px-2 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent bg-white"
                        >
                          <option value="">--</option>
                          {regionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={address.state}
                          onChange={(e) => setAddress(prev => ({ ...prev, state: e.target.value }))}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                          placeholder="State / province / region"
                        />
                      )}
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        {getPostalCodeLabel(address.country)}{isPostalCodeRequired(address.country) ? ' *' : ''}
                      </label>
                      <input
                        type="text"
                        value={address.zip}
                        onChange={(e) => setAddress(prev => ({ ...prev, zip: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        placeholder={getPostalCodeLabel(address.country)}
                      />
                    </div>
                  </div>
                  {isInternational && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      <TruckIcon className="w-4 h-4 flex-shrink-0" />
                      <span>International shipment — customs info (HS codes, country of origin) can be reviewed per item on the next step. A commercial invoice is generated automatically when the label is created.</span>
                    </div>
                  )}
                </div>
              )}

              {orderType === 'pickup' && (
                <div className="text-center py-6 bg-slate-50 rounded-xl border border-slate-200">
                  <ShoppingBagIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">This order will be marked for in-store pickup.</p>
                  <p className="text-xs text-slate-400 mt-1">No shipping address required.</p>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Order Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent resize-none"
                  placeholder="Internal notes about this order..."
                />
              </div>
            </div>
          )}

          {/* STEP 2: Items */}
          {currentStep === 'items' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Order Items</h3>
                <p className="text-sm text-slate-500">Search products by name, SKU, or barcode to add them.</p>
              </div>

              {isInternational && (
                <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <p className="font-medium">Shipping to {getCountryName(address.country)} — customs info required</p>
                  <p className="text-xs mt-1 text-amber-700">
                    Each item needs an HS tariff code and country of origin for the commercial invoice.
                    Values are pre-filled from the product catalog when available; anything left blank falls back to your defaults in Settings → International.
                  </p>
                </div>
              )}

              {/* Product Search */}
              <div className="relative" ref={productDropdownRef}>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    ref={productSearchRef}
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search by product name, SKU, or barcode..."
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                  />
                </div>

                {/* Product Results Dropdown */}
                {showProductDropdown && productResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-medium max-h-80 overflow-y-auto">
                    {productResults.map((product) => {
                      const hasVariants = product.variants && product.variants.length > 0;
                      return (
                        <div key={product.id}>
                          {!hasVariants ? (
                            <button
                              onClick={() => handleAddProduct(product)}
                              className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0 transition-colors"
                            >
                              {product.image_url ? (
                                <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <ShoppingBagIcon className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-slate-900 truncate">{product.title}</div>
                                <div className="text-xs text-slate-500">{product.sku || 'No SKU'} &middot; ${Number(product.price || 0).toFixed(2)}</div>
                              </div>
                              <PlusIcon className="w-5 h-5 text-brand-blue flex-shrink-0" />
                            </button>
                          ) : (
                            <div className="border-b border-slate-100 last:border-b-0">
                              <div className="px-4 py-2 bg-slate-50 text-xs font-medium text-slate-600 flex items-center gap-2">
                                {product.image_url && (
                                  <img src={product.image_url} alt="" className="w-6 h-6 rounded object-cover" />
                                )}
                                {product.title}
                              </div>
                              {product.variants!.map((variant) => (
                                <button
                                  key={variant.id}
                                  onClick={() => handleAddProduct(product, variant)}
                                  className="w-full px-4 py-2.5 pl-8 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors"
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm text-slate-800">{variant.title}</div>
                                    <div className="text-xs text-slate-500">
                                      {variant.sku || 'No SKU'} &middot; ${Number(variant.price || 0).toFixed(2)}
                                      {variant.inventory_quantity != null && (
                                        <span className={variant.inventory_quantity <= 0 ? 'text-red-500 ml-1' : ' ml-1'}>
                                          &middot; {variant.inventory_quantity} in stock
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <PlusIcon className="w-4 h-4 text-brand-blue flex-shrink-0" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {showProductDropdown && searchingProducts && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-medium p-4 text-center text-sm text-slate-500">
                    Searching products...
                  </div>
                )}

                {showProductDropdown && !searchingProducts && productSearch.length >= 2 && productResults.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-medium p-4 text-center text-sm text-slate-500">
                    No products found for &ldquo;{productSearch}&rdquo;
                  </div>
                )}
              </div>

              {/* Add Custom Item */}
              {!showCustomItemForm ? (
                <button
                  onClick={() => setShowCustomItemForm(true)}
                  className="flex items-center gap-2 text-sm font-medium text-brand-blue hover:text-brand-blue-hover transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Custom / Misc Item
                </button>
              ) : (
                <div className="border border-brand-blue border-dashed rounded-xl p-4 bg-blue-50/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-900">Custom Item</h4>
                    <button onClick={() => setShowCustomItemForm(false)} className="text-slate-400 hover:text-slate-600">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-700 mb-1">Item Name *</label>
                      <input
                        type="text"
                        value={customItem.title}
                        onChange={(e) => setCustomItem(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        placeholder="e.g. Custom Fragrance Blend, Rush Fee, etc."
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">SKU</label>
                      <input
                        type="text"
                        value={customItem.sku}
                        onChange={(e) => setCustomItem(prev => ({ ...prev, sku: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        placeholder="MISC"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Weight (oz)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={customItem.weight_oz}
                        onChange={(e) => setCustomItem(prev => ({ ...prev, weight_oz: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Quantity</label>
                      <input
                        type="number"
                        min="1"
                        value={customItem.quantity}
                        onChange={(e) => setCustomItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Unit Price ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={customItem.price}
                        onChange={(e) => setCustomItem(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                      />
                    </div>
                    {isInternational && (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">HS Tariff Code</label>
                          <input
                            type="text"
                            value={customItem.hs_tariff_code}
                            onChange={(e) => setCustomItem(prev => ({ ...prev, hs_tariff_code: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                            placeholder="e.g. 340600"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Country of Origin</label>
                          <select
                            value={customItem.country_of_origin}
                            onChange={(e) => setCustomItem(prev => ({ ...prev, country_of_origin: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent bg-white"
                          >
                            <option value="">Default</option>
                            {COUNTRIES.map(c => (
                              <option key={c.code} value={c.code}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      onClick={() => setShowCustomItemForm(false)}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddCustomItem}
                      disabled={!customItem.title.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Item
                    </button>
                  </div>
                </div>
              )}

              {/* Line Items Table */}
              {lineItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-300 rounded-xl">
                  <ShoppingBagIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No items added yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Search for products above or add a custom item.</p>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full divide-y divide-slate-200 table-fixed">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Product</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase w-20">Qty</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-24">Price</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-24">Total</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lineItems.map((item) => (
                        <tr key={item.key} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              {item.image_url ? (
                                <img src={item.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                                  <ShoppingBagIcon className="w-4 h-4 text-slate-400" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-slate-900 break-words">{item.title}</div>
                                <div className="text-xs text-slate-500">{item.sku}</div>
                                {isInternational && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <input
                                      type="text"
                                      value={item.hs_tariff_code || ''}
                                      onChange={(e) => handleUpdateLineItem(item.key, 'hs_tariff_code', e.target.value)}
                                      placeholder="HS code"
                                      title="HS tariff code for customs"
                                      className="w-28 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-brand-blue"
                                    />
                                    <select
                                      value={item.country_of_origin || ''}
                                      onChange={(e) => handleUpdateLineItem(item.key, 'country_of_origin', e.target.value)}
                                      title="Country of origin for customs"
                                      className="w-32 px-1 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-2 focus:ring-brand-blue bg-white"
                                    >
                                      <option value="">Origin: default</option>
                                      {COUNTRIES.map(c => (
                                        <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleUpdateLineItem(item.key, 'quantity', parseInt(e.target.value) || 1)}
                              className="w-16 mx-auto block text-center px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => handleUpdateLineItem(item.key, 'price', parseFloat(e.target.value) || 0)}
                              className="w-20 ml-auto block text-right px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                            />
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-slate-900 whitespace-nowrap">
                            ${(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleRemoveLineItem(item.key)}
                              className="text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{totalItems} item{totalItems !== 1 ? 's' : ''} &middot; {totalWeight.toFixed(1)} oz</span>
                      <span className="font-semibold text-slate-900">Subtotal: ${subtotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Review */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Review Order</h3>
                <p className="text-sm text-slate-500">Confirm the details before creating this order.</p>
              </div>

              {/* Customer Summary */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <UserIcon className="w-4 h-4 text-slate-500" />
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Customer</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Name:</span>{' '}
                    <span className="font-medium text-slate-900">{manualCustomer.first_name} {manualCustomer.last_name}</span>
                  </div>
                  {manualCustomer.email && (
                    <div>
                      <span className="text-slate-500">Email:</span>{' '}
                      <span className="font-medium text-slate-900">{manualCustomer.email}</span>
                    </div>
                  )}
                  {manualCustomer.phone && (
                    <div>
                      <span className="text-slate-500">Phone:</span>{' '}
                      <span className="font-medium text-slate-900">{manualCustomer.phone}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-500">Terms:</span>{' '}
                    <span className="font-medium text-slate-900">
                      {paymentTerms === 'prepaid' ? 'Prepaid' : paymentTerms === 'net30' ? 'Net 30' : 'Net 60'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Items Summary */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingBagIcon className="w-4 h-4 text-slate-500" />
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                    Items ({totalItems})
                  </h4>
                </div>
                <div className="space-y-2">
                  {lineItems.map(item => (
                    <div key={item.key} className="flex items-start justify-between text-sm gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <span className="text-slate-600 break-words">{item.title}</span>
                          <span className="text-slate-400 flex-shrink-0">× {item.quantity}</span>
                        </div>
                        {isInternational && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            HS: {item.hs_tariff_code?.trim() || 'default'} &middot; Origin: {item.country_of_origin || 'default'}
                          </div>
                        )}
                      </div>
                      <span className="font-medium text-slate-900 flex-shrink-0">${(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between text-sm font-semibold">
                    <span className="text-slate-700">Total ({totalWeight.toFixed(1)} oz)</span>
                    <span className="text-slate-900">${subtotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Shipping Summary */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TruckIcon className="w-4 h-4 text-slate-500" />
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                    {orderType === 'shipping' ? 'Shipping Address' : 'Pickup'}
                  </h4>
                </div>
                {orderType === 'shipping' ? (
                  <div className="text-sm text-slate-900">
                    <p>{address.line1}</p>
                    {address.line2 && <p>{address.line2}</p>}
                    <p>{address.city}{address.state ? `, ${address.state}` : ','} {address.zip}</p>
                    <p>{getCountryName(address.country)}</p>
                    {isInternational && (
                      <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                        International — a commercial invoice will be generated with the shipping label.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">In-store pickup</p>
                )}
              </div>

              {/* Notes */}
              {notes && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <DocumentTextIcon className="w-4 h-4 text-slate-500" />
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Notes</h4>
                  </div>
                  <p className="text-sm text-slate-600">{notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-slate-50 border-t border-slate-200 flex items-center justify-between rounded-b-xl">
          <div>
            {stepIndex > 0 && (
              <button
                onClick={prevStep}
                className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            {currentStep === 'review' ? (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2.5 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creating...' : 'Create Order'}
              </button>
            ) : (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="px-6 py-2.5 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
